import { usePromptModal } from '@affine/component';
import { useNavigateHelper } from '@affine/core/components/hooks/use-navigate-helper';
import { CollectionService } from '@affine/core/modules/collection';
import { useCollectionsFeatureEnabled } from '@affine/core/modules/streamlain';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { ViewLayersIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import { ActionButton } from './action-button';
import collectionListDark from './assets/collection-list.dark.png';
import collectionListLight from './assets/collection-list.light.png';
import { EmptyLayout } from './layout';
import type { UniversalEmptyProps } from './types';

export const EmptyCollections = (props: UniversalEmptyProps) => {
  const t = useI18n();
  const collectionsEnabled = useCollectionsFeatureEnabled();
  const collectionService = useService(CollectionService);
  const currentWorkspace = useService(WorkspaceService).workspace;

  const navigateHelper = useNavigateHelper();
  const { openPromptModal } = usePromptModal();

  const showAction = true;

  const handleCreateCollection = useCallback(() => {
    if (!collectionsEnabled) {
      return;
    }
    openPromptModal({
      title: t['com.streamlain.editCollection.saveCollection'](),
      label: t['com.streamlain.editCollectionName.name'](),
      inputOptions: {
        placeholder: t['com.streamlain.editCollectionName.name.placeholder'](),
      },
      children: t['com.streamlain.editCollectionName.createTips'](),
      confirmText: t['com.streamlain.editCollection.save'](),
      cancelText: t['com.streamlain.editCollection.button.cancel'](),
      confirmButtonOptions: {
        variant: 'primary',
      },
      onConfirm(name) {
        const id = collectionService.createCollection({ name });
        navigateHelper.jumpToCollection(currentWorkspace.id, id);
      },
    });
  }, [
    collectionService,
    collectionsEnabled,
    currentWorkspace.id,
    navigateHelper,
    openPromptModal,
    t,
  ]);

  if (!collectionsEnabled) {
    return null;
  }

  return (
    <EmptyLayout
      illustrationLight={collectionListLight}
      illustrationDark={collectionListDark}
      title={t['com.streamlain.empty.collections.title']()}
      description={t['com.streamlain.empty.collections.description']()}
      action={
        showAction ? (
          <ActionButton
            prefix={<ViewLayersIcon />}
            onClick={handleCreateCollection}
          >
            {t['com.streamlain.empty.collections.action.new-collection']()}
          </ActionButton>
        ) : null
      }
      {...props}
    />
  );
};
